import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import styled from "styled-components";

import Alert from "components/atoms/alert/Alert";
import Checkbox from "components/atoms/input/Checkbox";
import RadioButton from "components/atoms/input/RadioButton";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import AbstractModal from "components/page/modal/AbstractModal";
import AbstractModalFooter from "components/page/modal/AbstractModalFooter";
import CouponRegisterModal from "components/page/modal/CouponRegisterModal";

import {
    usePaymentBillingKeyCreate,
    usePaymentBillingKeyDelete,
    usePaymentCoupon,
    usePaymentDiscountPolicy,
    usePaymentPlan,
} from "hooks/queries/usePayment";
import { useSpacePaymentHistoryCreate } from "hooks/queries/usePayment";

import Constants from "utils/constants";
import { createTypedError } from "utils/validation";

import moment from "moment";

const Container = styled.div`
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
`;

const SubInfoBox = styled.div`
    margin-top: 40px;
    padding: 8px 16px;
    width: 100%;
    background-color: var(--color-InfoBox);
    align-items: center;
    border-radius: 4px;
`;

const InfoListWrap = styled.ul`
    margin: 0;
    padding: 0 0 0 16px;
`;

const SubInfoItem = styled.li`
    margin: 4px 0;
    line-height: 1.4;
    text-align: justify;
    color: var(--color-SubBlack);
`;

const Wrapper = styled.div`
    margin: 20px 0 0 0px;
`;

const OptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const Option = styled.div`
    display: block;
    align-items: center;
    margin-bottom: 15px;
    padding: 16px;
    border-radius: 4px;
    cursor: pointer;
    border: ${(props) => (props.selected ? "1px solid var(--color-Button1)" : "1px solid #ccc")};

    &:last-child {
        margin-bottom: 0;
    }
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
`;

const Description = styled(Text)`
    font-size: 1.2rem;
    color: var(--color-SubBlack);
    margin-top: 10px;
`;

const OptionTitleText = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    margin: 40px 0 20px 0;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const contentStyle = {
    variant: "body-md-400",
    color: "var(--color-Black)",
};

const listStyle = {
    variant: "body-sm-400",
    textAlign: "justify",
};

const PlanInfoArea = styled.div`
    text-align: center;
`;

const PlanSubText = styled(Text)`
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    margin-top: 12px;
`;

const PaymentMethodText = styled(Text)`
    font-size: 1.4rem;
    color: var(--color-Black);
`;

const CouponRegisterText = styled(Text)`
    font-size: 1.2rem;
    font-weight: 400;
    margin-left: auto;
    color: var(--color-Grey1);
`;

const CouponList = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
`;

const CouponItem = styled.div`
    display: flex;
    align-items: flex-start;
    padding: 12px;
    background-color: var(--color-InfoBox);
    cursor: pointer;

    &:not(:first-child) {
        margin-top: 2px;
    }
`;

const CouponContent = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 0px;
`;

const NoCouponText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})``;

const CardOptionWrapper = styled(Option)`
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
`;

const CardTermSelector = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    margin: 16px 0;
`;

const TermOption = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 12px;
    border-radius: 4px;
    height: 36px;
    border: ${(p) => (p.selected ? "1px solid var(--color-Button1)" : "1px solid var(--color-Line)")};
    cursor: pointer;
`;

const DiscountBadge = styled(Text)`
    font-size: 1rem;
    font-weight: 500;
    color: white;
    background-color: var(--color-Button1);
    border-radius: 4px;
    padding: 2px 6px;
    margin-left: 8px;
`;

const CardInfoBox = styled(SubInfoBox)`
    margin-top: 0;
    padding: 16px;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid var(--color-Line);
    margin: 12px 0;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 16px;
    width: 100%;
`;

const FeeSubText = styled(Text)`
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-Grey1);
`;

function SpacePaymentExtendModal(props) {
    const { space_uuid } = useParams();
    const navigate = useNavigate();

    const [paymentMethod, setPaymentMethod] = useState("card");

    // 쿠폰
    const couponQuery = usePaymentCoupon();

    const [couponList, setCouponList] = useState([]);
    const [showCouponRegisterModal, setShowCouponRegisterModal] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);

    const [cardTerm, setCardTerm] = useState("1m"); // '1m' or '3m'

    const discountPolicyQuery = usePaymentDiscountPolicy();
    const [longTermDiscounPolicy, setLongTermDiscountPolicy] = useState({});
    const [groupDiscountPolicy, setGroupDiscountPolicy] = useState({});

    const paymentPlanQuery = usePaymentPlan();
    const [plan, setPlan] = useState({});

    // 자동 연장 여부
    const [autoExtend, setAutoExtend] = useState(true);

    const billingKeyCreateMutation = usePaymentBillingKeyCreate();
    const billingKeyDeleteMutation = usePaymentBillingKeyDelete();
    const spacePaymentHistoryCreateMutation = useSpacePaymentHistoryCreate();

    const { setLoading } = useLoading();
    const { setToast } = useToast();

    useEffect(() => {
        if (couponQuery.data) {
            if (!isGroupPlan) {
                const filteredCoupons = couponQuery.data.filter((coupon) => !coupon.used_at);
                setCouponList(filteredCoupons.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));
            }
        }

        if (discountPolicyQuery.data) {
            if (isGroupPlan) {
                const groupPolicy = discountPolicyQuery.data.find(
                    (policy) => policy.internal_code === Constants.GROUP_DISCOUNT_POLICY,
                );
                setGroupDiscountPolicy(groupPolicy);
            }
            const longTermPolicy = discountPolicyQuery.data.find(
                (policy) => policy.internal_code === Constants.THREE_MONTH_DISCOUNT_POLICY,
            );
            setLongTermDiscountPolicy(longTermPolicy);
        }

        if (paymentPlanQuery.data) {
            if (isGroupPlan) {
                setPlan(paymentPlanQuery.data.find((plan) => plan.id === Constants.GROUP_PLAN));
            } else {
                setPlan(paymentPlanQuery.data.find((plan) => plan.id === Constants.PAID_PLAN));
            }
        }
    }, [couponQuery.data, discountPolicyQuery.data, paymentPlanQuery.data]);

    useEffect(() => {
        if (!isGroupPlan) {
            setPaymentMethod("card");
            if (couponList.length > 0) {
                setPaymentMethod("coupon");
                const latestCoupon = couponList.reduce((prev, current) =>
                    new Date(prev.updated_at) > new Date(current.updated_at) ? prev : current,
                );
                setSelectedCoupon(latestCoupon);
            } else if (props.isOnlyCouponUse) {
                setPaymentMethod("coupon");
            } else {
                setPaymentMethod("card");
            }
        }
    }, [couponList]);

    //region: 카드 결제 영역
    useEffect(() => {
        const jquery = document.createElement("script");
        jquery.src = "https://code.jquery.com/jquery-1.12.4.min.js";
        const iamport = document.createElement("script");
        iamport.src = "https://cdn.iamport.kr/js/iamport.payment-1.1.8.js";
        document.head.appendChild(jquery);
        document.head.appendChild(iamport);

        return () => {
            document.head.removeChild(jquery);
            document.head.removeChild(iamport);
        };
    }, []);

    const handleCardPayment = async () => {
        try {
            const { iamportId, customerUid } = buildMerchantInfo(props.user.email);
            const payload = buildIamportPayload({
                amount: paymentAmount,
                iamportId,
                customerUid,
            });

            const response = await requestIamportPayment(payload);

            setLoading(true, "결제 요청 중입니다.");
            let billingKey;
            try {
                billingKey = await billingKeyCreateMutation.mutateAsync(response);
            } catch (error) {
                throw createTypedError("billingKey", error);
            }

            const cardPaymentPayload = {
                started_at: payload.period.from,
                ended_at: payload.period.to,
                plan: plan.id,
                member_count: maxMemberCount,
                discount_policy: [
                    cardTerm === "3m" && longTermDiscounPolicy?.id,
                    isGroupPlan && groupDiscountPolicy?.id,
                ].filter(Boolean),
                amount: paymentAmount,
                status: Constants.PAYMENT_STATUS_COMPLETED,
                description: `${plan.name} 요금제 ${cardTerm === "3m" ? 3 : 1}개월 결제`,
                auto_extend: autoExtend,
                payment_type: isExtend() ? Constants.PAYMENT_TYPE_DIRECT_EXTEND : Constants.PAYMENT_TYPE_DIRECT_NEW,
                card: billingKey.id,
                iamport_id: iamportId,
                coupon: null,
                is_extend: isExtend(),
            };

            try {
                await spacePaymentHistoryCreateMutation.mutateAsync({
                    space_uuid,
                    data: cardPaymentPayload,
                });
            } catch (error) {
                throw createTypedError("subscription", error);
            }

            if (!autoExtend) {
                billingKeyDeleteMutation.mutate(billingKey.id);
            }

            setToast("요금제의 이용 기간이 연장되었습니다.", "info");
            props.exitModal();
        } catch (error) {
            console.log("payment error", error);
            handleCardError(error);
        } finally {
            setLoading(false);
        }
    };

    const buildMerchantInfo = (email) => {
        const randomNumber = Math.floor(Math.random() * 90000) + 10000;
        const emailPrefix = email.split("@")[0];
        return {
            iamportId: `extend_${emailPrefix}_${randomNumber}`,
            customerUid: `${emailPrefix}_${randomNumber}`,
        };
    };

    const buildIamportPayload = ({ amount, iamportId, customerUid }) => {
        const end = moment(props.paymentHistory.ended_at).add(1, "days");
        const now = moment();
        const from = (isExtend() ? end : now).format("YYYY-MM-DD");
        const to = moment(from)
            .add(cardTerm === "3m" ? 3 : 1, "months")
            .add(-1, "days")
            .format("YYYY-MM-DD");
        return {
            pg: `${process.env.REACT_APP_ENV !== "prd" ? "danal_tpay.9810030929" : "danal_tpay"}`,
            pay_method: "card",
            merchant_uid: iamportId,
            name: `${plan.name} > ${autoExtend ? "정기 결제" : "일반 결제"}`,
            period: {
                from: from,
                to: to,
            },
            amount: amount,
            customer_uid: customerUid,
            buyer_email: props.user.email,
        };
    };

    const requestIamportPayment = (payload) =>
        new Promise((resolve, reject) => {
            const { IMP } = window;
            IMP.init("imp65161342");
            document.getElementById("iamport-modal").style.display = "block";
            IMP.request_pay(payload, (response) => {
                document.getElementById("iamport-modal").style.display = "none";
                if (response.success) {
                    resolve(response);
                    return;
                }
                if (response.error_msg === "사용자가 결제를 취소하셨습니다") {
                    reject({ cancelled: true });
                    return;
                }
                reject(response);
            });
        });

    const handleCardError = (error) => {
        if (error?.cancelled) {
            return;
        }
        if (error?.type === "billingKey") {
            Alert("warn", "에러", "결제 정보 등록 중 오류가 발생했습니다. 결제 정보를 다시 확인해주세요.");
            return;
        }
        if (error?.type === "subscription") {
            Alert("warn", "에러", "구독 정보 변경 중 오류가 발생했습니다. 관리자에게 문의해주세요.");
            return;
        }
        if (error?.error_msg) {
            if (error.error_msg === "사용자가 결제를 취소하셨습니다") {
                return;
            }
            Alert("warn", "에러", "결제 중 오류가 발생했습니다. 결제 정보를 다시 확인해주세요.");
            return;
        }
        Alert("warn", "에러", "요청 중 오류가 발생했습니다. 관리자에게 문의해주세요.");
    };

    //region: 쿠폰 결제 영역
    const handleCouponPayment = async () => {
        if (selectedCoupon === null) {
            Alert("warn", "쿠폰 선택", "사용할 쿠폰을 선택해주세요.");
            return;
        }
        setLoading(true);
        try {
            const couponPaymentPayload = {
                started_at: null,
                ended_at: null,
                plan: plan.id,
                member_count: maxMemberCount,
                discount_policy: [],
                amount: 0,
                status: Constants.PAYMENT_STATUS_COMPLETED,
                description: `${selectedCoupon.name} 쿠폰 사용`,
                auto_extend: false,
                payment_type: isExtend() ? Constants.PAYMENT_TYPE_DIRECT_EXTEND : Constants.PAYMENT_TYPE_DIRECT_NEW,
                card: null,
                iamport_id: null,
                coupon: selectedCoupon.id,
                is_extend: isExtend(),
            };

            await spacePaymentHistoryCreateMutation.mutateAsync({
                space_uuid,
                data: couponPaymentPayload,
            });

            setToast("요금제의 이용 기간이 연장되었습니다.", "info");
            props.exitModal();
            navigate(`/${space_uuid}/management`);
        } catch (error) {
            Alert("warn", "에러", "쿠폰 사용 중 오류가 발생했습니다. 쿠폰 정보를 다시 확인해주세요.");
        } finally {
            setLoading(false);
        }
    };

    //region: 공통 유틸리티
    const isExtend = () => {
        const end = moment(props.paymentHistory.ended_at).add(1, "days");
        const now = moment();
        return now < end; // 오늘이 마지막 결제 이력의 끝나는 시점 안에 있으면 단순 연장(extend), 아니면 재구독
    };

    const isGroupPlan = props.paymentHistory.plan.id === Constants.GROUP_PLAN;
    const planAmount = plan?.price || 0;
    const longTermDiscountRate = Number(longTermDiscounPolicy?.discount_rate || 0);
    const groupDiscountRate = Number(groupDiscountPolicy?.discount_rate || 0);
    const maxMemberCount = props.user?.current_space?.max_member_count || 1;

    const monthlyAmount =
        planAmount -
        ((cardTerm === "3m" ? (planAmount * longTermDiscountRate) / 100 : 0) +
            (isGroupPlan ? (planAmount * groupDiscountRate) / 100 : 0));

    const paymentAmount = monthlyAmount * (cardTerm === "3m" ? 3 : 1) * (isGroupPlan ? maxMemberCount : 1);

    return (
        <Container>
            <PlanInfoArea>
                <PlanSubText>현재 사용 중인 요금제의 이용 기간을 연장합니다.</PlanSubText>
            </PlanInfoArea>
            <OptionTitleText>결제 옵션</OptionTitleText>
            <Wrapper>
                <OptionWrapper>
                    {/* 쿠폰 뷰 */}
                    {!isGroupPlan && (
                        <Option selected={paymentMethod === "coupon"}>
                            <HeaderRow onClick={() => setPaymentMethod("coupon")}>
                                <PaymentMethodText>쿠폰 결제</PaymentMethodText>
                                <CouponRegisterText
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowCouponRegisterModal(true);
                                    }}
                                >
                                    쿠폰 등록
                                </CouponRegisterText>
                            </HeaderRow>
                            {paymentMethod === "coupon" && (
                                <CouponList>
                                    {couponList.length === 0 ? (
                                        <NoCouponText>사용 가능한 쿠폰이 없습니다.</NoCouponText>
                                    ) : (
                                        couponList.map((coupon) => (
                                            <CouponItem
                                                key={coupon.id}
                                                selected={selectedCoupon?.number === coupon.number}
                                                onClick={() => setSelectedCoupon(coupon)}
                                            >
                                                <CouponContent>
                                                    <RadioButton
                                                        name="couponOption"
                                                        checked={selectedCoupon?.number === coupon.number}
                                                        onChange={() => setSelectedCoupon(coupon)}
                                                        label={coupon.name}
                                                        labelFontSize="1.4rem"
                                                    />
                                                    <Description>쿠폰 번호: {coupon.number}</Description>
                                                    <Description>
                                                        사용 기한: {moment(coupon.expired_at).format("YYYY.MM.DD")}
                                                    </Description>
                                                </CouponContent>
                                            </CouponItem>
                                        ))
                                    )}
                                </CouponList>
                            )}
                        </Option>
                    )}
                    {/* 카드 뷰 */}
                    {!props.isOnlyCouponUse && (
                        <CardOptionWrapper
                            selected={paymentMethod === "card"}
                            onClick={() => {
                                setPaymentMethod("card");
                            }}
                        >
                            <HeaderRow>
                                <PaymentMethodText>카드 결제</PaymentMethodText>
                            </HeaderRow>
                            {paymentMethod === "card" && (
                                <>
                                    {/* ── 기간 선택 */}
                                    <CardTermSelector>
                                        <TermOption selected={cardTerm === "1m"} onClick={() => setCardTerm("1m")}>
                                            <RadioButton
                                                name="cardTerm"
                                                checked={cardTerm === "1m"}
                                                onChange={() => setCardTerm("1m")}
                                                label="1개월 이용"
                                                labelFontSize="1.4rem"
                                            />
                                            {isGroupPlan && (
                                                <DiscountBadge>
                                                    {Number(groupDiscountPolicy?.discount_rate)}% 할인
                                                </DiscountBadge>
                                            )}
                                        </TermOption>

                                        <TermOption selected={cardTerm === "3m"} onClick={() => setCardTerm("3m")}>
                                            <RadioButton
                                                name="cardTerm"
                                                checked={cardTerm === "3m"}
                                                onChange={() => setCardTerm("3m")}
                                                label={`${longTermDiscounPolicy?.target_month}개월 이용`}
                                                labelFontSize="1.4rem"
                                            />
                                            <DiscountBadge>
                                                {Number(longTermDiscounPolicy?.discount_rate) +
                                                    (isGroupPlan ? Number(groupDiscountPolicy?.discount_rate) : 0)}
                                                % 할인
                                            </DiscountBadge>
                                        </TermOption>
                                    </CardTermSelector>

                                    {/* ── 요금 정보 박스 */}
                                    <CardInfoBox>
                                        <InfoRow>
                                            <Text {...contentStyle}>월 이용 요금 {isGroupPlan && "(1명)"}</Text>
                                            <Text {...contentStyle}>{monthlyAmount.toLocaleString()}원</Text>
                                        </InfoRow>

                                        <InfoRow style={{ paddingLeft: "8px" }}>
                                            <FeeSubText>기본 요금</FeeSubText>
                                            <FeeSubText>{plan.price?.toLocaleString()}원</FeeSubText>
                                        </InfoRow>

                                        <InfoRow style={{ paddingLeft: "8px" }}>
                                            <FeeSubText>
                                                장기 결제 할인 ({Number(longTermDiscounPolicy?.discount_rate)}%)
                                            </FeeSubText>
                                            <FeeSubText>
                                                {cardTerm === "3m" ? (
                                                    <>
                                                        -
                                                        {(
                                                            (plan?.price * longTermDiscounPolicy?.discount_rate) /
                                                            100
                                                        ).toLocaleString()}
                                                        원
                                                    </>
                                                ) : (
                                                    "해당없음"
                                                )}
                                            </FeeSubText>
                                        </InfoRow>

                                        {isGroupPlan && (
                                            <InfoRow style={{ paddingLeft: "8px" }}>
                                                <FeeSubText>
                                                    그룹 결제 할인 ({Number(groupDiscountPolicy?.discount_rate)}%)
                                                </FeeSubText>
                                                <FeeSubText>
                                                    -
                                                    {(
                                                        (plan?.price * groupDiscountPolicy?.discount_rate) /
                                                        100
                                                    ).toLocaleString()}
                                                    원
                                                </FeeSubText>
                                            </InfoRow>
                                        )}
                                        {isGroupPlan && (
                                            <InfoRow>
                                                <Text {...contentStyle}>멤버 수</Text>
                                                <Text {...contentStyle}>{maxMemberCount} 명</Text>
                                            </InfoRow>
                                        )}

                                        <InfoRow>
                                            <Text {...contentStyle}>이용 기간</Text>
                                            <Text {...contentStyle}>{cardTerm === "1m" ? "1개월" : "3개월"}</Text>
                                        </InfoRow>

                                        <Divider />

                                        <InfoRow>
                                            <Text {...contentStyle} fontWeight="bold">
                                                결제 금액
                                            </Text>
                                            <Text {...contentStyle} fontWeight="bold">
                                                {paymentAmount.toLocaleString()}원
                                            </Text>
                                        </InfoRow>
                                    </CardInfoBox>

                                    {/* ── 자동 연장 토글 */}
                                    <ToggleRow>
                                        <Text {...contentStyle}>이용 기간 만료 후 자동 연장</Text>
                                        <Checkbox
                                            checked={autoExtend}
                                            onChange={(e) => {
                                                setAutoExtend(e.target.checked);
                                            }}
                                            margin={"0 0 0 auto"}
                                        />
                                    </ToggleRow>
                                </>
                            )}
                        </CardOptionWrapper>
                    )}
                </OptionWrapper>
            </Wrapper>

            <SubInfoBox>
                <InfoListWrap>
                    <SubInfoItem>
                        <Text {...listStyle}>표시된 모든 금액은 부가세(VAT)가 포함된 금액입니다.</Text>
                    </SubInfoItem>
                    <SubInfoItem>
                        <Text {...listStyle}>결제 완료 후 환불은 제공되지 않습니다.</Text>
                    </SubInfoItem>
                </InfoListWrap>
            </SubInfoBox>

            <AbstractModalFooter
                leftBtnText={"취소"}
                rightBtnText={"결제하기"}
                leftOnClick={props.exitModal}
                rightOnClick={() => {
                    if (paymentMethod === "coupon") {
                        handleCouponPayment();
                    } else {
                        handleCardPayment();
                    }
                }}
            />
            {showCouponRegisterModal && (
                <AbstractModal modalTitle="Add Coupon" width={480} exitModal={(e) => setShowCouponRegisterModal(false)}>
                    <CouponRegisterModal exitModal={(e) => setShowCouponRegisterModal(false)} />
                </AbstractModal>
            )}
            <div
                style={{
                    display: "none",
                    overFlow: "scroll",
                    position: "fixed",
                    zIndex: 10000,
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    padding: "120px 0",
                    background: "rgba(0,0,0,.48)",
                    boxSizing: "border-box",
                }}
                id={"iamport-modal"}
            ></div>
        </Container>
    );
}

export default SpacePaymentExtendModal;
